/* ==========================================================================
   OUTLETZONE — theme.js
   Core runtime. No framework, no dependencies, no polyfills.

   Everything here is either (a) an enhancement over markup that already works
   without JS, or (b) a genuine interaction that cannot be expressed in HTML.
   Nothing renders content that the server did not already send.
   ========================================================================== */
(function () {
  'use strict';

  const OZ = window.OZ || {};

  /* ------------------------------------------------------------ helpers */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const announce = (message) => {
    const region = document.getElementById('oz-live-region');
    if (!region) return;
    region.textContent = '';
    // Re-setting on the next frame guarantees screen readers re-announce even
    // when the same string is repeated.
    requestAnimationFrame(() => { region.textContent = message; });
  };

  const formatMoney = (cents) => {
    const amount = (cents / 100).toLocaleString('fr-MA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${amount} MAD`;
  };

  const debounce = (fn, wait) => {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const trapFocus = (container, firstFocus) => {
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
      'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const nodes = $$(selector, container).filter((el) => el.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handler);
    (firstFocus || container).focus();
    return () => container.removeEventListener('keydown', handler);
  };

  OZ.helpers = { $, $$, announce, formatMoney, debounce, trapFocus };
  window.OZ = OZ;

  /* ============================================================== TOAST */
  class UiToast extends HTMLElement {
    show(message, duration = 2600) {
      this.innerHTML = `<svg class="icon" aria-hidden="true"><use href="#icon-check"></use></svg><span></span>`;
      this.querySelector('span').textContent = message;
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-visible'));
      clearTimeout(this._t);
      this._t = setTimeout(() => {
        this.classList.remove('is-visible');
        setTimeout(() => { this.hidden = true; }, 220);
      }, duration);
    }
  }
  customElements.define('ui-toast', UiToast);

  OZ.toast = (message) => {
    const el = $('ui-toast');
    if (el) el.show(message);
    announce(message);
  };

  /* ========================================================= STICKY HEADER */
  // Adds a class once the page has scrolled, so the header can tighten its
  // rule. Passive listener + rAF guard keeps this off the main-thread budget.
  const headerWrap = $('.header-wrap');
  if (headerWrap) {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        headerWrap.classList.toggle('header-wrap--scrolled', window.scrollY > 8);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ================================================================ DRAWER */
  class UiDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.releaseFocus = null;
      this.opener = null;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('drawer__overlay')) {
          this.close();
        }
      });

      this._onKey = (e) => { if (e.key === 'Escape') this.close(); };
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      document.addEventListener('keydown', this._onKey);
      const first = this.querySelector('[data-drawer-focus]') || this.panel;
      this.releaseFocus = trapFocus(this.panel, first);
      this.dispatchEvent(new CustomEvent('drawer:open'));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      document.removeEventListener('keydown', this._onKey);
      if (this.releaseFocus) this.releaseFocus();
      if (this.opener && document.contains(this.opener)) this.opener.focus();
      this.dispatchEvent(new CustomEvent('drawer:close'));
    }
  }
  customElements.define('ui-drawer', UiDrawer);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (!drawer) return;
    e.preventDefault();
    drawer.open(trigger);
  });

  /* ============================================================ CART STATE */
  // Single source of truth for cart mutations. Any component can call these
  // and every subscriber re-renders from the returned cart object.
  const cartSubscribers = new Set();

  OZ.cart = {
    subscribe(fn) { cartSubscribers.add(fn); return () => cartSubscribers.delete(fn); },

    publish(cart) { cartSubscribers.forEach((fn) => fn(cart)); },

    async add(items) {
      const res = await fetch(`${OZ.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: Array.isArray(items) ? items : [items] })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || OZ.strings.cartError);
      }
      const cart = await this.get();
      this.publish(cart);
      return cart;
    },

    async change(payload) {
      const res = await fetch(`${OZ.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(OZ.strings.cartError);
      const cart = await res.json();
      this.publish(cart);
      return cart;
    },

    async get() {
      const res = await fetch(`${OZ.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      return res.json();
    },

    async updateNote(note) {
      return fetch(`${OZ.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });
    }
  };

  // Keep every cart-count bubble in sync regardless of which component changed it.
  OZ.cart.subscribe((cart) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  });

  /* ============================================================= ACCORDION */
  // Native <details> already works. This only enforces single-open behaviour
  // where a group opts in, and is entirely optional.
  $$('[data-accordion-group]').forEach((group) => {
    const items = $$('details', group);
    items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((other) => { if (other !== item) other.open = false; });
      });
    });
  });

  /* ======================================================= RECENTLY VIEWED */
  // Written on idle so it never competes with interaction for main-thread time.
  const RV_KEY = 'oz:recently-viewed';

  OZ.recentlyViewed = {
    read() {
      try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; }
      catch (_) { return []; }
    },
    push(handle) {
      if (!handle) return;
      try {
        const list = this.read().filter((h) => h !== handle);
        list.unshift(handle);
        localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
      } catch (_) { /* private mode — silently skip */ }
    }
  };

  const currentProduct = document.body.getAttribute('data-product-handle');
  if (OZ.settings && OZ.settings.recentlyViewed && currentProduct) {
    const write = () => OZ.recentlyViewed.push(currentProduct);
    'requestIdleCallback' in window ? requestIdleCallback(write) : setTimeout(write, 400);
  }

  /* ================================================== RECENTLY VIEWED LIST */
  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      const exclude = this.dataset.exclude || '';
      const handles = OZ.recentlyViewed.read().filter((h) => h !== exclude).slice(0, 4);
      if (!handles.length) { this.closest('[data-recently-viewed-section]')?.remove(); return; }

      // Shopify's search endpoint accepts a handle query, so this needs no
      // custom app proxy and stays a single request.
      const query = handles.map((h) => `handle:${h}`).join(' OR ');
      const url = `${OZ.routes.search}?q=${encodeURIComponent(query)}&type=product&view=cards`;

      fetch(url)
        .then((r) => (r.ok ? r.text() : Promise.reject()))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const cards = doc.querySelectorAll('[data-product-card]');
          if (!cards.length) { this.closest('[data-recently-viewed-section]')?.remove(); return; }
          const frag = document.createDocumentFragment();
          cards.forEach((c) => frag.appendChild(c));
          this.innerHTML = '';
          this.appendChild(frag);
          this.closest('[data-recently-viewed-section]')?.removeAttribute('hidden');
        })
        .catch(() => this.closest('[data-recently-viewed-section]')?.remove());
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* ============================================================== QUANTITY */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty]');
        if (!btn) return;
        e.preventDefault();
        const step = btn.dataset.qty === 'plus' ? 1 : -1;
        const min = parseInt(this.input.min || '1', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
        if (next === parseInt(this.input.value, 10)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ============================================================== PREFETCH */
  // Speculative prefetch on intent. Respects Save-Data and slow connections,
  // and never fires on touch (where hover is not a signal of intent).
  if (OZ.settings && OZ.settings.prefetch && matchMedia('(hover: hover)').matches) {
    const conn = navigator.connection;
    const allowed = !conn || (!conn.saveData && !/2g/.test(conn.effectiveType || ''));
    if (allowed) {
      const seen = new Set();
      document.addEventListener('mouseover', (e) => {
        const link = e.target.closest('a[href*="/products/"]');
        if (!link || seen.has(link.href) || link.origin !== location.origin) return;
        seen.add(link.href);
        const l = document.createElement('link');
        l.rel = 'prefetch';
        l.href = link.href;
        l.as = 'document';
        document.head.appendChild(l);
      }, { passive: true });
    }
  }

  /* ========================================================== SEARCH PANEL */
  class SearchPanel extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-close]')) this.close();
      });
      this._onKey = (e) => { if (e.key === 'Escape') this.close(); };
    }

    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      document.body.classList.add('is-locked');
      document.addEventListener('keydown', this._onKey);
      this.release = trapFocus(this, this.input);
      // Loaded only when the user actually reaches for search.
      if (!window.OZ._searchLoaded) {
        window.OZ._searchLoaded = true;
        const s = document.createElement('script');
        s.src = this.dataset.script;
        s.defer = true;
        document.head.appendChild(s);
      }
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      document.removeEventListener('keydown', this._onKey);
      if (this.release) this.release();
      if (this.opener) this.opener.focus();
    }
  }
  customElements.define('search-panel', SearchPanel);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-search-open]');
    if (!trigger) return;
    e.preventDefault();
    $('search-panel')?.open(trigger);
  });
})();
